import React, { useCallback, useMemo, useState } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import { v4 } from "uuid";
import { useMeCore } from "../../../../../cores/me";
import { useWebFormCore } from "../../../../../cores/webForm";
import { useIntl } from "react-intl";
import {
  CompanyInputType,
  ContactInputType,
  InputType,
} from "../../../../../cores/webForm/constants/InputType.constant";
import { FormColumnWidth } from "../../../../../cores/webForm/constants/FormColumnWidth.constant";
import { CheckboxWembii } from "../../../CheckboxWembii";
import { InputWembii } from "../../../InputWembii";
import { PanelWembii } from "../../../PanelWembii";
import { TitleWembii } from "../../../TitleWembii";
import Icofont from "react-icofont";
import { useContactCore } from "../../../../../cores/contacts";
import {
  hasCategoryOptions,
  isCategoryFormInput,
  isChoiceFormInput,
  isFormInputCategoryOption,
  normalizeCategoryOptions,
  normalizeFormInputOptions,
  splitFormInputOptions,
} from "../../../../../cores/webForm/utils/formRender.utils";
import CategoryOptionsEditor from "./CategoryOptionsEditor";
import AcceptanceLinksEditor from "./AcceptanceLinksEditor";

const TERMS_TEXT =
  "Acepto los Términos y Condiciones y confirmo haber leído la Política de Privacidad y Protección de Datos.";

export default function InputsForm() {
  const intl = useIntl();
  const { company } = useMeCore();
  const { webForm, putFormInputs, addInput, removeInput, updateInput } =
    useWebFormCore();
  const { customFields } = useContactCore();
  const [hasChange, setHasChange] = useState(false);

  const handlerAddInput = useCallback(async () => {
    addInput({
      id: v4(),
      type: InputType.TEXT.id,
      placeholder: `${intl.formatMessage({ id: "app.editForm.helpTxt" })}`,
      required: true,
      columnWidth: FormColumnWidth.FULL,
    });
    setHasChange(true);
  }, [addInput, setHasChange, intl]);

  const handleConfigChange = useCallback(
    (input) => {
      updateInput(input);
      setHasChange(true);
    },
    [updateInput]
  );

  const handleTypeChange = useCallback(
    (input, type) => {
      const nextInput = { ...input, type };
      if (type === InputType.SELECT.id && !input.options?.length) {
        nextInput.options = ["Opción 1", "Opción 2", "Opción 3"];
      }
      if (type === InputType.CATEGORY_SELECT.id && !hasCategoryOptions(input.options)) {
        nextInput.options = [
          {
            label: `${intl.formatMessage({ id: "app.editForm.category" })} 1`,
            subcategories: [
              `${intl.formatMessage({ id: "app.editForm.subcategory" })} 1`,
            ],
          },
        ];
      }
      if (type === InputType.ACCEPTANCE.id) {
        nextInput.placeholder = input.placeholder || TERMS_TEXT;
        nextInput.required = true;
      }
      handleConfigChange(nextInput);
    },
    [handleConfigChange, intl]
  );

  const handleOptionsChange = useCallback(
    (input, rawOptions: string) => {
      handleConfigChange({
        ...input,
        options: splitFormInputOptions(rawOptions),
      });
    },
    [handleConfigChange]
  );

  const handlerRemoveInput = useCallback(
    async (id) => {
      removeInput(id);
      setHasChange(true);
    },
    [removeInput, setHasChange]
  );

  const handleChangeInput = useCallback(async () => {
    const inputsToSave = webForm.inputs.map((input) => {
      if (!isCategoryFormInput(input.type)) return input;
      return {
        ...input,
        options: normalizeCategoryOptions(input.options),
      };
    });
    await putFormInputs(company?.id!, webForm.form?.id!, inputsToSave);
    setHasChange(false);
  }, [putFormInputs, company, webForm, setHasChange]);

  const options = useMemo(
    () => [
      ...Object.values(ContactInputType).map((type) => ({
        value: `${type.id}`,
        label: intl.formatMessage({ id: type.name }),
      })),
      ...customFields.map((field) => ({
        value: `${field.id}`,
        label: `${intl.formatMessage({
          id: "app.chatConfigCommertial.customTxt",
        })} - ${field.name}`,
      })),
      ...Object.values(CompanyInputType).map((type) => ({
        value: `${type.id}`,
        label: intl.formatMessage({ id: type.name }),
      })),
      ...Object.values(InputType).map((type) => ({
        value: `${type.id}`,
        label: intl.formatMessage({ id: type.name }),
      })),
    ],
    [customFields, intl]
  );

  return (
    <Container className="p-0">
      <PanelWembii shadow className="mb-4 p-4 web-form-editor-ai__card web-form-editor-ai__card--inputs">
        <TitleWembii Tag="h5" color="primary">
          {intl.formatMessage({ id: "app.editForm.tittle3" })}
        </TitleWembii>
        <div className="w-100">
          {webForm.inputs.map((input) => (
            <Row key={input.id} className="web-form-editor-ai__input-row web-form-editor-ai__input-controls">
              <Col xl="3" lg="4" md="12" xs="12">
                <InputWembii
                  type="select"
                  name="inputType"
                  id={`inputType-${input.id}`}
                  value={input.type}
                  flat
                  mb={false}
                  onChange={(v) => handleTypeChange(input, v)}
                >
                  {options.map((type) => (
                    <option
                      key={`${type.value}${input.type}`}
                      value={type.value}
                    >
                      {type.label}
                    </option>
                  ))}
                </InputWembii>
              </Col>
              <Col xl="4" lg="4" md="7" xs="12">
                <InputWembii
                  type="text"
                  name="placeholder"
                  id={`placeholder-${input.id}`}
                  placeholder={intl.formatMessage({ id: "app.editForm.helpTxt" })}
                  value={input.placeholder}
                  flat
                  mb={false}
                  onChange={(v) =>
                    handleConfigChange({ ...input, placeholder: v })
                  }
                />
              </Col>
              <Col xl="3" lg="3" md="5" xs="12">
                <InputWembii
                  type="select"
                  name={`columnWidth-${input.id}`}
                  id={`columnWidth-${input.id}`}
                  title={intl.formatMessage({ id: "app.editForm.columnWidth" })}
                  value={input.columnWidth || FormColumnWidth.FULL}
                  flat
                  mb={false}
                  onChange={(v) =>
                    handleConfigChange({
                      ...input,
                      columnWidth:
                        v === FormColumnWidth.HALF
                          ? FormColumnWidth.HALF
                          : FormColumnWidth.FULL,
                    })
                  }
                >
                  <option value={FormColumnWidth.FULL}>
                    {intl.formatMessage({ id: "app.editForm.columnWidth.full" })}
                  </option>
                  <option value={FormColumnWidth.HALF}>
                    {intl.formatMessage({ id: "app.editForm.columnWidth.half" })}
                  </option>
                </InputWembii>
              </Col>
              <Col xl="2" lg="1" md="12" xs="12">
                <div className="web-form-editor-ai__input-actions">
                  <CheckboxWembii
                    label={intl.formatMessage({
                      id: "app.editForm.requiredCheck",
                    })}
                    name={`required-${input.id}`}
                    checked={input.required}
                    mb={false}
                    onChange={(v) =>
                      handleConfigChange({ ...input, required: v })
                    }
                  />
                  <div
                    onClick={() => handlerRemoveInput(input.id)}
                    className="text-danger pointer"
                  >
                    <Icofont icon="ui-delete" />
                  </div>
                </div>
              </Col>
              {isChoiceFormInput(input.type) && (
                <Col xs="12" className="mb-3">
                  <InputWembii
                    type="textarea"
                    name={`options-${input.id}`}
                    id={`options-${input.id}`}
                    label={intl.formatMessage({ id: "app.editForm.options" })}
                    placeholder={intl.formatMessage({
                      id: "app.editForm.optionsPlaceholder",
                    })}
                    value={normalizeFormInputOptions(input.options).join("\n")}
                    flat
                    onChange={(v) => handleOptionsChange(input, v)}
                  />
                </Col>
              )}
              {isCategoryFormInput(input.type) && (
                <CategoryOptionsEditor
                  categories={
                    Array.isArray(input.options)
                      ? input.options.filter(isFormInputCategoryOption)
                      : []
                  }
                  onChange={(categories) =>
                    handleConfigChange({ ...input, options: categories })
                  }
                />
              )}
              {input.type === InputType.ACCEPTANCE.id && (
                <Col xs="12">
                  <AcceptanceLinksEditor
                    inputId={input.id}
                    options={input.options}
                    onChange={(options) =>
                      handleConfigChange({ ...input, options })
                    }
                  />
                </Col>
              )}
            </Row>
          ))}
          <div className="text-center">
            <Button type="button" color="primary" className="web-form-editor-ai__icon-btn" onClick={handlerAddInput}>
              <Icofont icon="plus" />
            </Button>
            <Button
              type="button"
              color="primary"
              className="ml-2 web-form-editor-ai__cta"
              onClick={handleChangeInput}
              disabled={!hasChange}
            >
              {intl.formatMessage({ id: "app.editForm.saveConfigBtn" })}
            </Button>
          </div>
        </div>
      </PanelWembii>
    </Container>
  );
}
